import * as THREE from 'three';
import { config } from './config.js';



export let starField = null;
export let sunMesh = null;
export let ambientLight = null;
export let directionalLight = null;



export function setupLights(scene) {

    ambientLight = new THREE.AmbientLight(config.lights.ambientColor, config.lights.ambient);
    scene.add(ambientLight);

    directionalLight = new THREE.DirectionalLight(config.lights.directionalColor, config.lights.directional);
    directionalLight.position.set(config.sun.posX, config.sun.posY, config.sun.posZ);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;

    

    const shadowSize = config.lights.shadowCameraSize;
    directionalLight.shadow.camera.left = -shadowSize;
    directionalLight.shadow.camera.right = shadowSize;
    directionalLight.shadow.camera.top = shadowSize;
    directionalLight.shadow.camera.bottom = -shadowSize;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 10000;
    directionalLight.shadow.bias = -0.0001;
    scene.add(directionalLight);

    createSun(scene);
    return directionalLight;

}



function createSun(scene) {
    const sunGeo = new THREE.SphereGeometry(config.sun.size, 32, 32);
    const sunMat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(config.sun.color.r / 255, config.sun.color.g / 255, config.sun.color.b / 255)
    });

    sunMesh = new THREE.Mesh(sunGeo, sunMat);
    sunMesh.position.set(config.sun.posX, config.sun.posY, config.sun.posZ);
    scene.add(sunMesh);


}



export function createStarfield(scene) {

    const starGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(config.stars.count * 3);
    const colors = new Float32Array(config.stars.count * 3);
    const sizes = new Float32Array(config.stars.count);



    for (let i = 0; i < config.stars.count; i++) {

        const i3 = i * 3;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(Math.random() * 2 - 1);
        const radius = 2500 + Math.random() * 4500;



        positions[i3] = radius * Math.sin(phi) * Math.cos(theta);
        positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
        positions[i3 + 2] = radius * Math.cos(phi);



        const colorChoice = Math.random();

        if (colorChoice > 0.98) {

            colors[i3] = 0.8 + Math.random() * 0.2;
            colors[i3 + 1] = 0.7 + Math.random() * 0.3;
            colors[i3 + 2] = 1.0;

        } else if (colorChoice > 0.96) {

            colors[i3] = 1.0;
            colors[i3 + 1] = 0.85 + Math.random() * 0.15;
            colors[i3 + 2] = 0.6 + Math.random() * 0.4;

        } else if (colorChoice > 0.88) {

            colors[i3] = 1.0;
            colors[i3 + 1] = 0.4 + Math.random() * 0.3;
            colors[i3 + 2] = 0.2 + Math.random() * 0.3;

        } else {

            const brightness = 0.85 + Math.random() * 0.15;

            colors[i3] = brightness;
            colors[i3 + 1] = brightness * 0.95;
            colors[i3 + 2] = brightness * 0.9;

        }


        const sizeVariation = Math.random();

        if (sizeVariation > 0.97) {
            sizes[i] = (3 + Math.random() * 4) * config.stars.size;
        } else if (sizeVariation > 0.9) {
            sizes[i] = (2 + Math.random() * 2) * config.stars.size;
        } else {
            sizes[i] = (0.5 + Math.random() * 1.5) * config.stars.size;
        }

    }


    starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeo.setAttribute('starColor', new THREE.BufferAttribute(colors, 3));
    starGeo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));


    const starMat = new THREE.ShaderMaterial({

        uniforms: {

            time: { value: 0 },
            brightness: { value: config.stars.brightness },
            twinkleSpeed: { value: config.stars.twinkleSpeed },
            starSize: { value: config.stars.size }

        },

        vertexShader: `

            attribute float size;
            attribute vec3 starColor;
            varying vec3 vColor;
            uniform float time;
            uniform float brightness;
            uniform float twinkleSpeed;
            uniform float starSize;

            void main() {

                vColor = starColor * brightness;
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                float twinkle = sin(time * twinkleSpeed + position.x * 0.01 + position.y * 0.02) * 0.2 + 0.8;
                gl_PointSize = size * twinkle * (350.0 / -mvPosition.z) * (starSize / 3.5);
                gl_Position = projectionMatrix * mvPosition;

            }

        `,

        fragmentShader: `

            varying vec3 vColor;

            void main() {

                vec2 center = gl_PointCoord - vec2(0.5);
                float dist = length(center);
                if (dist > 0.5) discard;
                float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
                float glow = pow(alpha, 0.5);
                gl_FragColor = vec4(vColor * glow, alpha);

            }

        `,

        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false

    });

    starField = new THREE.Points(starGeo, starMat);
    scene.add(starField);
}



export function setupFloor(scene) {

    const floorGeo = new THREE.PlaneGeometry(10000, 10000);
    const floorMat = new THREE.MeshStandardMaterial({ 

        color: config.floor.color,
        metalness: config.floor.metalness,
        roughness: config.floor.roughness

    });


    const floor = new THREE.Mesh(floorGeo, floorMat);

    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.position.y = 0;

    scene.add(floor);
    return floor;
}



export function updateEnvironment(time) {

    if (starField) {

        starField.material.uniforms.time.value = time;
        starField.material.uniforms.brightness.value = config.stars.brightness;
        starField.material.uniforms.twinkleSpeed.value = config.stars.twinkleSpeed;
        starField.material.uniforms.starSize.value = config.stars.size;

    }

    

    if (ambientLight) {

        ambientLight.intensity = config.lights.ambient;
        ambientLight.color.setHex(config.lights.ambientColor);

    }

    

    if (directionalLight) {

        directionalLight.intensity = config.lights.directional;
        directionalLight.color.setHex(config.lights.directionalColor);
        directionalLight.position.set(config.sun.posX, config.sun.posY, config.sun.posZ);
        

        const shadowSize = config.lights.shadowCameraSize;
        directionalLight.shadow.camera.left = -shadowSize;
        directionalLight.shadow.camera.right = shadowSize;
        directionalLight.shadow.camera.top = shadowSize;
        directionalLight.shadow.camera.bottom = -shadowSize;
        directionalLight.shadow.camera.updateProjectionMatrix();

    }

    

    if (sunMesh && config.sun.enabled) {

        sunMesh.visible = true;
        sunMesh.position.set(config.sun.posX, config.sun.posY, config.sun.posZ);
        sunMesh.scale.setScalar(config.sun.size / 15);
        sunMesh.material.color.setRGB(config.sun.color.r / 255, config.sun.color.g / 255, config.sun.color.b / 255);

    
        const intensity = config.sun.intensity;
        sunMesh.material.color.multiplyScalar(intensity);

    } else if (sunMesh) {

        sunMesh.visible = false;

    }



}